import { supabase } from '../../lib/supabase';
import type { AiReviewStatus } from '../../components/AiReviewReport';

export interface StatementFile {
  name: string;
  type: string;
  url: string;
}

export type StatementCheckState =
  | { phase: 'idle'; files: StatementFile[] }
  | { phase: 'running'; files: StatementFile[] }
  | { phase: 'done'; files: StatementFile[]; overall: AiReviewStatus; messages: string[] }
  | { phase: 'failed'; files: StatementFile[]; message: string };

export const MAX_STATEMENT_FILES = 6;
const MAX_FILE_MB = 15;
const CLIENT_TIMEOUT_MS = 130000;

export async function uploadStatementFile(file: File, contractTempId: string): Promise<StatementFile> {
  if (file.size > MAX_FILE_MB * 1024 * 1024) throw new Error(`Arquivo muito grande. Máx ${MAX_FILE_MB}MB.`);
  const ext = file.name.split('.').pop();
  const path = `manual/${contractTempId}/extrato-${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
  const { error } = await supabase.storage.from('contract-files').upload(path, file, { contentType: file.type, upsert: true });
  if (error) throw new Error('Erro no envio do arquivo. Tente novamente.');
  const { data } = supabase.storage.from('contract-files').getPublicUrl(path);
  return { name: file.name, type: file.type, url: data.publicUrl };
}

export async function runStatementCheck(
  files: StatementFile[],
  contractTempId: string,
): Promise<Extract<StatementCheckState, { phase: 'done' | 'failed' }>> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return { phase: 'failed', files, message: 'Sessão expirada. Entre novamente no sistema.' };

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CLIENT_TIMEOUT_MS);
  try {
    const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/statement-ai-check`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
      },
      body: JSON.stringify({ contractTempId, files }),
      signal: controller.signal,
    });
    const json = await resp.json().catch(() => null);
    if (resp.ok && json?.status === 'completed' && ['ok', 'attention', 'problem'].includes(json.overall) && Array.isArray(json.messages)) {
      return { phase: 'done', files, overall: json.overall, messages: json.messages };
    }
    return { phase: 'failed', files, message: json?.error || 'Não foi possível analisar o extrato agora.' };
  } catch (err) {
    const aborted = err instanceof DOMException && err.name === 'AbortError';
    return { phase: 'failed', files, message: aborted ? 'A análise do extrato demorou demais. Tente novamente.' : 'Falha de conexão. Tente novamente.' };
  } finally {
    clearTimeout(timer);
  }
}
